import { CONTENT_FITNESS_ASSESSMENT_MODE } from "../../../enums/content-fitness";
import { CONTENT_FITNESS_FRAMEWORK_VERSION } from "../version";
import { anchors, type ContentFitnessSubcriterion, deferred } from "./types";

export const JOURNEY_USABILITY_ACTION_SUBCRITERIA = [
  {
    key: "journey_usability_action.intent_journey_stage_fit",
    version: CONTENT_FITNESS_FRAMEWORK_VERSION,
    label: "Intent & journey-stage fit",
    parentMetricKey: "journey_usability_action",
    order: 1,
    assessmentMode: CONTENT_FITNESS_ASSESSMENT_MODE.RUBRIC,
    pilot: true,
    definition:
      "Scores whether page purpose, depth and next-step logic match the likely user intent and journey stage.",
    evidenceGuidance:
      "Use URL, page type, inferred intent, journey stage, headings, copy depth, CTA type and adjacent journey links.",
    applicabilityNotes:
      "For multi-intent pages, score the dominant intended job and note secondary conflicts. Do not invent full multi-page journey continuity without evidence.",
    anchors: anchors(
      "Page purpose, depth and actions fundamentally miss the likely intent or journey stage.",
      "Intent/stage fit is weak; users are likely to be confused, under-served or pushed to the wrong next step.",
      "Basic intent fit exists, but depth, sequence or next-step logic has material gaps.",
      "Page purpose, depth and journey logic fit the likely user stage well.",
      "Page is precisely tuned to intent and stage, with clear progression to the next useful step.",
    ),
    objectiveChecks: [
      "page type recorded",
      "journey stage inferred",
      "relevant next-stage link/CTA exists",
    ],
  },
  deferred(
    "journey_usability_action.audience_fit",
    "Audience fit",
    "journey_usability_action",
    2,
    CONTENT_FITNESS_ASSESSMENT_MODE.RUBRIC,
    "Scores whether the page speaks to the segment or persona most likely to land there.",
  ),
  deferred(
    "journey_usability_action.scannability_formatting",
    "Scannability & formatting",
    "journey_usability_action",
    3,
    CONTENT_FITNESS_ASSESSMENT_MODE.HYBRID,
    "Scores whether users can scan and absorb the page through headings, lists, short paragraphs and useful formatting.",
  ),
  deferred(
    "journey_usability_action.navigation_link_quality",
    "Navigation & link quality",
    "journey_usability_action",
    4,
    CONTENT_FITNESS_ASSESSMENT_MODE.HYBRID,
    "Scores whether headings and links help users orient and move through the estate.",
  ),
  {
    key: "journey_usability_action.action_path_friction_proof",
    version: CONTENT_FITNESS_FRAMEWORK_VERSION,
    label: "Action path, friction & proof",
    parentMetricKey: "journey_usability_action",
    order: 5,
    assessmentMode: CONTENT_FITNESS_ASSESSMENT_MODE.HYBRID,
    pilot: true,
    definition:
      "Scores whether the page presents one appropriate primary action, makes it prominent and low-friction, and places reassurance near the action when needed.",
    evidenceGuidance:
      "Use CTAs, button labels, forms, field counts, flow steps, proof points, reassurance copy, screenshots, layout and page type.",
    applicabilityNotes:
      "For informational pages, the action may be a next-read, compare, save, contact or support path rather than a sale. Do not infer conversion performance without analytics.",
    anchors: anchors(
      "No appropriate action path exists, or the action is broken, hidden or fundamentally mismatched to the page.",
      "Action path exists weakly, with unclear labels, poor prominence, high friction or missing reassurance where needed.",
      "Users can act, but material gaps in clarity, prominence, friction or proof reduce confidence.",
      "Action path is clear, prominent and reasonably low-friction, with relevant reassurance where it matters.",
      "The page makes the right action obvious, compelling and easy, with proportionate proof and minimal unnecessary friction.",
    ),
    objectiveChecks: [
      "CTA present",
      "CTA label value",
      "above-fold prominence",
      "form field count",
      "broken CTA link",
      "proof near CTA",
    ],
  },
] as const satisfies readonly ContentFitnessSubcriterion[];
